import React, { useMemo, useState } from 'react';
import type { FosterHome } from '../../lib/fosterAdapters';
import type { SortDir } from '../../lib/types';
import { formatPhoneNumber } from '../../lib/utils';
import { SkeletonRows } from '../../components/SkeletonRow/SkeletonRow';
import Button from '../../components/Button/Button';
import Pagination from '../../components/Pagination/Pagination';
import CollapsibleSection from './CollapsibleSection';
import TableHead, { type TableColumn } from './TableHead';
import { ACTION_COLUMN_WIDTH } from './columnWidths';
import useClientPagination from '../../hooks/useClientPagination';
import styles from './Devices.module.scss';

type RosterSortKey = 'foster' | 'status' | 'placements';

/**
 * Widths are chosen so the Action column starts and ends exactly where the Action column of
 * "Devices with a Foster" does — the two tables stack on the Fosters tab, and a View button
 * that sat a little left of the Actions menu above it read as a misalignment rather than a
 * different table. The lead four therefore have to fill `100 - ACTION_COLUMN_WIDTH`.
 */
export const ROSTER_COLUMNS: TableColumn<RosterSortKey>[] = [
  { label: 'Foster', width: '32%', sortKey: 'foster' },
  { label: 'Phone', width: '19%' },
  { label: 'Status', width: '20%', sortKey: 'status' },
  { label: 'Placements', width: '16%', sortKey: 'placements' },
  { label: 'Action', width: `${ACTION_COLUMN_WIDTH}%` },
];

/**
 * Sorts a copy so the memoized roster stays stable. Counts sort descending-first, since "who is
 * fostering right now" and "who has taken the most" are the questions worth a click; names go
 * A–Z first.
 */
function sortHomes(list: FosterHome[], sort: { key: RosterSortKey; dir: SortDir } | null): FosterHome[] {
  if (!sort) {
    return list;
  }
  const mult = sort.dir === 'asc' ? 1 : -1;
  return [...list].sort((a, b) => {
    let cmp: number;
    if (sort.key === 'foster') {
      cmp = a.fullName.localeCompare(b.fullName);
    } else if (sort.key === 'status') {
      cmp = a.activePlacementCount - b.activePlacementCount;
    } else {
      cmp = a.placementCount - b.placementCount;
    }
    // Ties keep a stable, readable order rather than whatever the server returned.
    return cmp !== 0 ? cmp * mult : a.fullName.localeCompare(b.fullName);
  });
}

interface FosterRosterTableProps {
  homes: FosterHome[];
  loading: boolean;
  hasError: boolean;
  onRetry: () => void;
  pageSize: number;
  onView: (home: FosterHome) => void;
  onAdd: () => void;
}

/**
 * The rescue's saved foster homes — the people on file, including ones fostering nothing right now.
 * Distinct from "Devices with a Foster", which lists placements. Paginated locally.
 */
export default function FosterRosterTable({
  homes,
  loading,
  hasError,
  onRetry,
  pageSize,
  onView,
  onAdd,
}: FosterRosterTableProps) {
  const [sort, setSort] = useState<{ key: RosterSortKey; dir: SortDir } | null>(null);
  const sorted = useMemo(() => sortHomes(homes, sort), [homes, sort]);
  const pagination = useClientPagination(sorted, pageSize);

  function handleSort(key: RosterSortKey) {
    setSort((prev) => {
      if (prev && prev.key === key) {
        return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      }
      // Counts open on the largest; names on A–Z.
      return { key, dir: key === 'foster' ? 'asc' : 'desc' };
    });
    pagination.resetPage();
  }
  const showSkeleton = loading && homes.length === 0 && !hasError;

  return (
    <CollapsibleSection
      title="Foster Roster"
      count={homes.length}
      description="Fosters you've saved. Their household stays on file between placements, so Safe Zone and WiFi carry forward."
      headerAction={
        <Button variant="green" size="sm" onClick={onAdd}>
          <svg
            className={styles.headerBtnIcon}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.6"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Add Foster
        </Button>
      }
    >
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <TableHead columns={ROSTER_COLUMNS} sortKey={sort?.key ?? null} sortDir={sort?.dir} onSort={handleSort} />
          <tbody>
            {hasError ? (
              <tr>
                <td colSpan={ROSTER_COLUMNS.length} className={styles.errorRow} role="alert">
                  <span className={styles.errorRowContent}>
                    <span>Could not load your foster roster.</span>
                    <Button variant="ghost" size="sm" onClick={onRetry}>
                      Retry
                    </Button>
                  </span>
                </td>
              </tr>
            ) : showSkeleton ? (
              <SkeletonRows count={3} columns={ROSTER_COLUMNS.length} />
            ) : homes.length === 0 ? (
              <tr className={styles.emptyRow}>
                <td colSpan={ROSTER_COLUMNS.length}>
                  <div className={styles.emptyHeading}>No saved fosters yet</div>
                  <div className={styles.emptyBody}>
                    Add the fosters you already work with, or save one as you assign a device.
                  </div>
                  <Button variant="green" onClick={onAdd}>
                    Add your first foster
                  </Button>
                </td>
              </tr>
            ) : (
              pagination.pageItems.map((home) => (
                <tr key={home.id}>
                  <td>
                    <div className={styles.cellMain}>{home.fullName}</div>
                    {home.email && <div className={styles.cellSub}>{home.email}</div>}
                  </td>
                  <td className={styles.nowrap}>{home.phone ? formatPhoneNumber(home.phone) : '—'}</td>
                  <td>
                    {home.activePlacementCount > 0 ? (
                      `Fostering (${home.activePlacementCount})`
                    ) : (
                      <span className={styles.textMuted}>Not fostering</span>
                    )}
                  </td>
                  <td>{home.placementCount}</td>
                  <td>
                    <div className={styles.rowActions}>
                      <Button variant="ghost" size="sm" onClick={() => onView(home)}>
                        View
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {pagination.totalPages > 1 && (
        <div className={styles.paginationWrapper}>
          <Pagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            totalItems={pagination.totalCount}
            pageSize={pageSize}
            onPageChange={pagination.goToPage}
          />
        </div>
      )}
    </CollapsibleSection>
  );
}
